import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import {
  SquareArrowDown,
  GalleryVertical,
  TextCursorInput,
  Globe,
  Smartphone,
  ScanEye,
  Rotate3d,
  ChevronDown,
  Search,
  ClipboardCopy,
  Check,
  Inbox,
  PackageCheck,
  ToyBrick,
  Component,
  Layers,
  AppWindow,
  Funnel,
  FunnelX,
  Image,
  Shapes,
  ShipWheel,
  PanelTop,
  PanelBottomOpen,
  LayoutDashboard,
  MessageSquareMore,
  X,
} from 'lucide-react'
import type { ShowcaseSettings, ComponentsFilterState, FilteredComponent, ComponentFilterStatus } from '../../../pages/ComponentShowcase'
import { loadStatusData, saveStatusData, type ReviewStatusData } from '../utils/reviewStatusStorage'
import {
  componentDefinitions,
  componentTypes,
  componentCategories,
  getComponentsByType,
  type ComponentType,
  type ComponentCategory,
} from '../componentClassifications'

interface ComponentsSectionProps {
  settings: ShowcaseSettings
  onNavigate: (componentId: string) => void
  filterState: ComponentsFilterState | null
  onFilterStateChange: (state: ComponentsFilterState) => void
  onFilteredComponentsChange: (components: FilteredComponent[]) => void
}

// Status types
type ReviewStatus = 'default' | 'complete'
type ReviewCategory = 'webTokenCleanup' | 'nativeTokenCleanup' | 'accessibility' | 'animation'


// Type icons for display (rendered at 16x16 for section headers)
const typeIcons: Record<string, React.ReactNode> = {
  'Buttons': <SquareArrowDown className="h-4 w-4" />,
  'Tiles': <GalleryVertical className="h-4 w-4" />,
  'Input': <TextCursorInput className="h-4 w-4" />,
  'Images': <Image className="h-4 w-4" />,
  'Navigation': <ShipWheel className="h-4 w-4" />,
  'Frames': <PanelTop className="h-4 w-4" />,
  'Overlays': <PanelBottomOpen className="h-4 w-4" />,
  'Sections': <LayoutDashboard className="h-4 w-4" />,
  'Misc': <Shapes className="h-4 w-4" />,
}

// Type icon components (for filter dropdown)
const typeIconComponents: Record<string, React.FC<{ className?: string; style?: React.CSSProperties }>> = {
  'Buttons': SquareArrowDown,
  'Tiles': GalleryVertical,
  'Input': TextCursorInput,
  'Images': Image,
  'Navigation': ShipWheel,
  'Frames': PanelTop,
  'Overlays': PanelBottomOpen,
  'Sections': LayoutDashboard,
  'Misc': Shapes,
}

// Category icon components (Blocks, Components, Containers, Chrome)
const categoryIconComponents: Record<ComponentCategory, React.FC<{ className?: string; style?: React.CSSProperties }>> = {
  'Blocks': ToyBrick,
  'Components': Component,
  'Containers': Layers,
  'Chrome': AppWindow,
}

// Status filter icons
const statusFilterIcons: Record<ComponentFilterStatus, React.FC<{ className?: string; style?: React.CSSProperties }>> = {
  'pending': Inbox,
  'complete': PackageCheck,
}

// Status filter colors (Tailwind -500 shades)
const statusFilterColors: Record<ComponentFilterStatus, string> = {
  'pending': '#2b7fff',        // blue-500
  'complete': '#00bc7d',       // emerald-500
}

// Status cycle order
const statusCycle: ReviewStatus[] = ['default', 'complete']

// Status colors for category icons
const statusColors: Record<ReviewStatus, string> = {
  default: '#a1a1a1',   // neutral-400
  complete: '#00bc7d'   // emerald-500
}

// Review category icons (used in status section with status-based coloring)
const reviewCategoryIconComponents: Record<ReviewCategory, React.FC<{ className?: string; style?: React.CSSProperties }>> = {
  webTokenCleanup: Globe,
  nativeTokenCleanup: Smartphone,
  accessibility: ScanEye,
  animation: Rotate3d,
}

// Tooltips for each category
const categoryTooltips: Record<ReviewCategory, string> = {
  webTokenCleanup: 'Web Token Cleanup',
  nativeTokenCleanup: 'Native Token Cleanup',
  accessibility: 'Accessibility',
  animation: 'Animation',
}

// Categories in order
const categories: ReviewCategory[] = ['webTokenCleanup', 'nativeTokenCleanup', 'accessibility', 'animation']

// Check if a component is fully reviewed (all categories are 'complete')
function isComponentFullyReviewed(componentId: string, statusData: ReviewStatusData): boolean {
  const status = statusData[componentId]
  if (!status) return false
  return categories.every(category => status[category] === 'complete')
}

// Text highlighting component for search matches
function HighlightedText({
  text,
  highlight,
  className = ''
}: {
  text: string
  highlight: string
  className?: string
}) {
  if (!highlight.trim()) {
    return <span className={className}>{text}</span>
  }

  const regex = new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
  const parts = text.split(regex)

  return (
    <span className={className}>
      {parts.map((part, index) =>
        regex.test(part) ? (
          <span key={index} style={{ backgroundColor: '#fe9a00' }}>{part}</span>
        ) : (
          <span key={index}>{part}</span>
        )
      )}
    </span>
  )
}

// Helper to determine a component's filter status
function getComponentFilterStatus(componentId: string, statusData: ReviewStatusData): ComponentFilterStatus {
  const status = statusData[componentId]
  if (!status) return 'pending'

  const allComplete = categories.every(cat => status[cat] === 'complete')
  return allComplete ? 'complete' : 'pending'
}

// Filter option type
interface FilterOption {
  value: string
  label: string
  icon?: React.FC<{ className?: string; style?: React.CSSProperties }>
  iconColor?: string
}

// Filter group for the combined dropdown
interface FilterGroup {
  label: string
  options: FilterOption[]
  selectedValues: Set<string>
  onToggle: (value: string, shiftKey: boolean) => void
}

// Combined Filter Dropdown Component
interface CombinedFilterDropdownProps {
  groups: FilterGroup[]
  hasActiveFilters: boolean
  onClearFilters: () => void
}

function CombinedFilterDropdown({ groups, hasActiveFilters, onClearFilters }: CombinedFilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const handleMouseEnter = () => {
    setIsOpen(true)
  }

  const handleMouseLeave = () => {
    setIsOpen(false)
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <div
        ref={dropdownRef}
        style={{ position: 'relative' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: isOpen ? '#171717' : '#0a0a0a',
            border: 'none',
            cursor: 'pointer',
            fontFamily: "'Figtree', sans-serif",
            fontSize: '14px',
            fontWeight: 630,
            color: '#fafafa',
            padding: '4px 8px',
            borderRadius: isOpen ? '6px 6px 0 0' : '6px',
          }}
        >
          <Funnel style={{ width: 14, height: 14 }} />
          <span>Filter</span>
          <ChevronDown className="h-3 w-3" />
        </button>

        {isOpen && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              backgroundColor: '#171717',
              borderRadius: '0 6px 6px 6px',
              padding: '6px',
              zIndex: 100,
              minWidth: '380px',
            }}
          >
            {groups.map((group, groupIndex) => (
              <div key={group.label}>
                {/* Section header */}
                <div
                  style={{
                    fontFamily: "'Figtree', sans-serif",
                    fontSize: '10px',
                    fontWeight: 600,
                    color: '#737373',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    padding: groupIndex === 0 ? '4px 4px 6px 4px' : '12px 4px 6px 4px',
                  }}
                >
                  {group.label}
                </div>
                {/* Options grid */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${Math.min(group.options.length, 4)}, 1fr)`,
                    gap: '4px',
                  }}
                >
                  {group.options.map((option) => {
                    const isSelected = group.selectedValues.has(option.value)
                    const IconComponent = option.icon
                    return (
                      <button
                        key={option.value}
                        onClick={(e) => group.onToggle(option.value, e.shiftKey)}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          fontFamily: "'Figtree', sans-serif",
                          fontSize: '12px',
                          fontWeight: isSelected ? 630 : 500,
                          color: isSelected ? '#ffffff' : '#a1a1a1',
                          backgroundColor: isSelected ? '#404040' : '#262626',
                          border: isSelected ? '1px solid #737373' : '1px solid transparent',
                          borderRadius: '4px',
                          padding: '8px 0',
                          cursor: 'pointer',
                          minWidth: '90px',
                        }}
                      >
                        {IconComponent && <IconComponent style={{ width: 16, height: 16, color: option.iconColor }} />}
                        {option.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {hasActiveFilters && (
        <button
          onClick={onClearFilters}
          title="Clear all filters"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: '6px',
          }}
        >
          <FunnelX style={{ width: 14, height: 14, color: '#fb2c36' }} />
        </button>
      )}
    </div>
  )
}

// Component Card
interface ComponentCardProps {
  component: { id: string; label: string; types: ComponentType[] }
  componentCategories: ComponentCategory[]
  searchTerm: string
  isSelected: boolean
  statusData: ReviewStatusData
  onNavigate: (id: string) => void
  onStatusClick: (componentId: string, category: ReviewCategory, shiftKey: boolean) => void
  onCommentClick: (componentId: string) => void
}

function ComponentCard({
  component,
  componentCategories: compCategories,
  searchTerm,
  isSelected,
  statusData,
  onNavigate,
  onStatusClick,
  onCommentClick,
}: ComponentCardProps) {
  const [isPressed, setIsPressed] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const getStatus = (cat: ReviewCategory): ReviewStatus => {
    const statusRecord = statusData[component.id] as Record<string, string> | undefined
    return (statusRecord?.[cat] as ReviewStatus) || 'default'
  }

  // Get the component's filter status for styling
  const filterStatus = getComponentFilterStatus(component.id, statusData)

  // Determine card background based on selection state and filter status
  const getCardBg = () => {
    if (isSelected) {
      return isPressed
        ? '#432dd7'  // indigo-700
        : isHovered
          ? '#372aac'  // indigo-800
          : '#312c85' // indigo-900
    }
    // Complete status gets emerald background
    if (filterStatus === 'complete') {
      return isPressed
        ? '#065f46'  // emerald-800
        : isHovered
          ? '#004f3b'  // emerald-900
          : '#002c22' // emerald-950
    }
    // Pending uses neutral
    return isPressed
      ? '#525252'  // neutral-600
      : isHovered
        ? '#404040'  // neutral-700
        : '#262626' // neutral-800
  }

  // Determine card border based on selection state and filter status
  const getCardBorder = () => {
    if (isSelected) {
      return '1px solid #615fff'
    }
    if (filterStatus === 'pending') {
      return '1px solid #404040'  // neutral-700
    }
    if (filterStatus === 'complete') {
      return '1px solid #00bc7d'  // emerald-500
    }
    return '1px solid #404040'  // neutral-700
  }

  // Determine card text color based on filter status
  const getCardTextColor = () => {
    if (filterStatus === 'complete') {
      return '#ecfdf5'  // emerald-50
    }
    return '#ffffff'  // white (default)
  }

  return (
    <div
      style={{
        minWidth: '240px',
        height: '96px',
        borderRadius: '9px',
        border: getCardBorder(),
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: getCardBg(),
        transition: 'background-color 0.15s ease',
      }}
    >
      {/* Top Section - Clickable to navigate */}
      <button
        onClick={() => onNavigate(component.id)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => { setIsHovered(false); setIsPressed(false) }}
        onMouseDown={() => setIsPressed(true)}
        onMouseUp={() => setIsPressed(false)}
        style={{
          height: '60px',
          backgroundColor: 'transparent',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: '2px',
          padding: '0 12px',
          cursor: 'pointer',
          border: 'none',
          width: '100%',
          position: 'relative',
        }}
      >
        {/* Category and Type icons row (right-aligned) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: '100%',
            gap: '6px',
          }}
        >
          {/* Category icons first */}
          {compCategories.map((cat) => {
            const CatIcon = categoryIconComponents[cat]
            return (
              <CatIcon
                key={cat}
                style={{ width: 18, height: 18, color: 'rgba(255, 255, 255, 0.6)' }}
              />
            )
          })}
          {/* Type icons second */}
          {component.types.map((type) => {
            const TypeIcon = typeIconComponents[type]
            return TypeIcon ? (
              <TypeIcon
                key={type}
                style={{ width: 18, height: 18, color: 'rgba(255, 255, 255, 0.6)' }}
              />
            ) : null
          })}
        </div>
        <span
          style={{
            fontFamily: "'Menlo', monospace",
            fontSize: '15px',
            fontWeight: 'normal',
            color: getCardTextColor(),
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
        >
          <HighlightedText text={component.label} highlight={searchTerm} />
        </span>
      </button>

      {/* Bottom Section - Status icons */}
      <div
        style={{
          height: '36px',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '18px',
        }}
      >
        {categories.map((cat) => {
          const IconComponent = reviewCategoryIconComponents[cat]
          const status = getStatus(cat)
          const color = statusColors[status]

          return (
            <button
              key={cat}
              onClick={(e) => onStatusClick(component.id, cat, e.shiftKey)}
              title={`${categoryTooltips[cat]} (${status})`}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconComponent
                style={{ color, width: 20, height: 20 }}
              />
            </button>
          )
        })}
        <button
          onClick={(e) => {
            e.stopPropagation()
            onCommentClick(component.id)
          }}
          title={statusData[component.id]?.comments || 'Add comment'}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: '2px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <MessageSquareMore
            style={{ color: statusData[component.id]?.comments ? '#60a5fa' : '#a1a1a1', width: 18, height: 18 }}
          />
        </button>
      </div>
    </div>
  )
}

// Default status filter options (shown by default)
const defaultStatusOptions: ComponentFilterStatus[] = ['pending']

export default function ComponentsSection({ settings: _settings, onNavigate, filterState, onFilterStateChange, onFilteredComponentsChange }: ComponentsSectionProps) {
  const [statusData, setStatusData] = useState<ReviewStatusData>(() => loadStatusData())
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [exportButtonHovered, setExportButtonHovered] = useState(false)
  const [exportButtonPressed, setExportButtonPressed] = useState(false)
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [copied, setCopied] = useState(false)

  // Comment popup state
  const [commentPopupComponentId, setCommentPopupComponentId] = useState<string | null>(null)
  const [commentPopupValue, setCommentPopupValue] = useState('')
  const commentTextareaRef = useRef<HTMLTextAreaElement>(null)
  const commentPopupRef = useRef<HTMLDivElement>(null)

  const handleCommentClick = useCallback((componentId: string) => {
    const currentComment = statusData[componentId]?.comments || ''
    setCommentPopupValue(currentComment)
    setCommentPopupComponentId(componentId)
    setTimeout(() => commentTextareaRef.current?.focus(), 50)
  }, [statusData])

  const handleCommentSave = useCallback(() => {
    if (commentPopupComponentId === null) return
    setStatusData(prev => ({
      ...prev,
      [commentPopupComponentId]: {
        ...(prev[commentPopupComponentId] || {
          webTokenCleanup: 'default' as ReviewStatus,
          nativeTokenCleanup: 'default' as ReviewStatus,
          accessibility: 'default' as ReviewStatus,
          animation: 'default' as ReviewStatus,
        }),
        comments: commentPopupValue,
      }
    }))
    setCommentPopupComponentId(null)
  }, [commentPopupComponentId, commentPopupValue])

  const handleCommentCancel = useCallback(() => {
    setCommentPopupComponentId(null)
  }, [])

  // Close comment popup when clicking outside
  useEffect(() => {
    if (!commentPopupComponentId) return
    const handleClickOutside = (e: MouseEvent) => {
      if (commentPopupRef.current && !commentPopupRef.current.contains(e.target as Node)) {
        handleCommentSave()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [commentPopupComponentId, handleCommentSave])

  // Export status data for all components tracked in localStorage (Round 3)
  const handleExportStatus = async () => {
    // Export the full statusData from localStorage - this includes any components
    // that have been added to the preview but not yet to the JSON file
    const jsonString = JSON.stringify(statusData, null, 2)
    const exportText = `Update apps/web/src/components/showcase-new/ComponentsReviewRound3.json with:
\`\`\`json
${jsonString}
\`\`\``
    try {
      await navigator.clipboard.writeText(exportText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
    }
  }

  // Get available types from componentClassifications
  const availableTypes = useMemo(() => {
    return [...componentTypes]
  }, [])

  // Get available categories from componentClassifications
  const availableCategories = useMemo(() => {
    return [...componentCategories]
  }, [])

  // Initialize filters from lifted state or defaults
  const [searchTerm, setSearchTerm] = useState(() => filterState?.searchTerm ?? '')
  const [statusFilters, setStatusFilters] = useState<Set<ComponentFilterStatus>>(
    () => filterState?.statusFilters ? new Set(filterState.statusFilters) : new Set(defaultStatusOptions)
  )
  const [typeFilters, setTypeFilters] = useState<Set<ComponentType>>(
    () => filterState?.typeFilters ? new Set(filterState.typeFilters as Set<ComponentType>) : new Set(componentTypes)
  )
  const [categoryFilters, setCategoryFilters] = useState<Set<ComponentCategory>>(
    () => filterState?.categoryFilters ? new Set(filterState.categoryFilters as Set<ComponentCategory>) : new Set(componentCategories)
  )
  // Sync filter state to parent whenever it changes
  useEffect(() => {
    onFilterStateChange({
      searchTerm,
      statusFilters: new Set(statusFilters) as Set<ComponentFilterStatus>,
      typeFilters: new Set(typeFilters),
      categoryFilters: new Set(categoryFilters),
    })
  }, [searchTerm, statusFilters, typeFilters, categoryFilters, onFilterStateChange])

  // Helper to reset all filters to default
  const clearFilters = useCallback(() => {
    setStatusFilters(new Set(defaultStatusOptions))
    setTypeFilters(new Set(componentTypes))
    setCategoryFilters(new Set(componentCategories))
  }, [])

  // Check if any filters differ from the default state
  const hasActiveFilters = useMemo(() => {
    // Status filters are active if they differ from the default (pending)
    const statusDiffersFromDefault = statusFilters.size !== defaultStatusOptions.length ||
      !defaultStatusOptions.every(opt => statusFilters.has(opt))
    return statusDiffersFromDefault ||
      typeFilters.size < componentTypes.length ||
      categoryFilters.size < componentCategories.length
  }, [statusFilters, typeFilters, categoryFilters])

  // Sync to localStorage whenever status changes
  useEffect(() => {
    saveStatusData(statusData)
  }, [statusData])

  // Reset selected index when search term changes
  useEffect(() => {
    if (searchTerm.trim()) {
      setSelectedIndex(0)
    } else {
      setSelectedIndex(-1)
    }
  }, [searchTerm])

  const handleStatusClick = (componentId: string, category: ReviewCategory, shiftKey: boolean) => {
    setStatusData(prev => {
      const existingComponentStatus = prev[componentId] || {
        webTokenCleanup: 'default' as ReviewStatus,
        nativeTokenCleanup: 'default' as ReviewStatus,
        accessibility: 'default' as ReviewStatus,
        animation: 'default' as ReviewStatus,
      }

      // Shift+click resets to default
      if (shiftKey) {
        return {
          ...prev,
          [componentId]: {
            ...existingComponentStatus,
            [category]: 'default' as ReviewStatus
          }
        }
      }

      const statusRecord = prev[componentId] as Record<string, string> | undefined
      const currentStatus = statusRecord?.[category] || 'default'
      const currentIndex = statusCycle.indexOf(currentStatus as ReviewStatus)
      const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % statusCycle.length
      const nextStatus = statusCycle[nextIndex]

      return {
        ...prev,
        [componentId]: {
          ...existingComponentStatus,
          [category]: nextStatus
        }
      }
    })
  }

  // Check if type filters are active (not all types selected)
  const isTypeFilterActive = useMemo(() => {
    return typeFilters.size < componentTypes.length
  }, [typeFilters])

  // Filter components based on search term, status filters, type filters, and category filters
  const filteredSections = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase().trim()

    // Get components grouped by type from the classification system
    const typeGroups = getComponentsByType()

    return typeGroups
      .map(group => ({
        title: group.type,
        components: group.components.filter(comp => {
          // Search filter
          if (lowerSearch) {
            const matchesSearch = comp.label.toLowerCase().includes(lowerSearch)
            if (!matchesSearch) return false
          }

          // Status filter
          if (statusFilters.size > 0) {
            const compStatus = getComponentFilterStatus(comp.id, statusData)
            if (!statusFilters.has(compStatus)) return false
          }

          // Type filter (component must have at least one matching type)
          if (typeFilters.size > 0) {
            const hasMatchingType = comp.types.some(type => typeFilters.has(type))
            if (!hasMatchingType) return false
          }

          // Category filter (component must have at least one matching category)
          if (categoryFilters.size > 0) {
            const hasMatchingCategory = comp.categories.some(cat => categoryFilters.has(cat))
            if (!hasMatchingCategory) return false
          }

          return true
        })
        // Sort components alphabetically by label within each group
        .sort((a, b) => a.label.localeCompare(b.label))
      }))
      .filter(section => section.components.length > 0)
  }, [searchTerm, statusFilters, typeFilters, categoryFilters, statusData])

  // Flat sorted list of all filtered components (for default view)
  const flatSortedComponents = useMemo(() => {
    return filteredSections
      .flatMap(section => section.components.map(comp => ({
        ...comp,
        componentType: section.title
      })))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [filteredSections])

  // Get flat list of filtered components for keyboard navigation
  // Uses alphabetical order in default view, section-grouped order when filtering by type
  const flatFilteredComponents = useMemo(() => {
    if (isTypeFilterActive) {
      // When filtering by type, use section-grouped order
      return filteredSections.flatMap(section =>
        section.components.map(comp => ({
          ...comp,
          componentType: section.title
        }))
      )
    }
    // Default view: use alphabetical order
    return flatSortedComponents
  }, [filteredSections, flatSortedComponents, isTypeFilterActive])

  // Sorted search results: prioritize name matches, then alphabetical
  const searchSortedComponents = useMemo(() => {
    if (!searchTerm.trim()) return flatFilteredComponents

    const lowerSearch = searchTerm.toLowerCase().trim()

    return [...flatFilteredComponents].sort((a, b) => {
      const aNameMatch = a.label.toLowerCase().includes(lowerSearch)
      const bNameMatch = b.label.toLowerCase().includes(lowerSearch)

      if (aNameMatch && !bNameMatch) return -1
      if (!aNameMatch && bNameMatch) return 1

      return a.label.localeCompare(b.label)
    })
  }, [flatFilteredComponents, searchTerm])

  // Sync filtered components to parent for preview navigation
  useEffect(() => {
    onFilteredComponentsChange(
      flatFilteredComponents.map(comp => ({
        id: comp.id,
        label: comp.label,
        section: comp.componentType
      }))
    )
  }, [flatFilteredComponents, onFilteredComponentsChange])

  // Keyboard navigation handler
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (flatFilteredComponents.length === 0) return

    if (e.key === 'Tab') {
      e.preventDefault()
      if (selectedIndex === -1) {
        // Enter selection mode - select first component
        setSelectedIndex(0)
      } else if (e.shiftKey) {
        // Shift+Tab - go to previous
        setSelectedIndex(prev =>
          prev <= 0 ? flatFilteredComponents.length - 1 : prev - 1
        )
      } else {
        // Tab - go to next
        setSelectedIndex(prev =>
          prev >= flatFilteredComponents.length - 1 ? 0 : prev + 1
        )
      }
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault()
      const selectedComponent = flatFilteredComponents[selectedIndex]
      if (selectedComponent) {
        onNavigate(selectedComponent.id)
      }
    } else if (e.key === 'Escape' && selectedIndex >= 0) {
      // Exit selection mode
      setSelectedIndex(-1)
    }
  }, [flatFilteredComponents, selectedIndex, onNavigate])

  // Attach keyboard listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  // Track which component is at which index in the flat list
  const getComponentIndex = (componentId: string): number => {
    return flatFilteredComponents.findIndex(c => c.id === componentId)
  }

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Search/Filter Bar */}
      <div
        id="components-filters"
        style={{
          height: '60px',
          backgroundColor: '#000000',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          position: 'relative',
          borderBottom: '1px solid #262626',
        }}
      >
        {/* Filters - Left */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '0 0 auto', opacity: isSearchFocused ? 0 : 1, pointerEvents: isSearchFocused ? 'none' : 'auto', transition: 'opacity 200ms ease', zIndex: 1 }}>
          <CombinedFilterDropdown
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
            groups={[
              {
                label: 'Status',
                options: [
                  { value: 'pending', label: 'Pending', icon: statusFilterIcons['pending'] },
                  { value: 'complete', label: 'Complete', icon: statusFilterIcons['complete'], iconColor: statusFilterColors['complete'] },
                ],
                selectedValues: statusFilters as Set<string>,
                onToggle: (value, shiftKey) => {
                  setStatusFilters(prev => {
                    if (shiftKey) {
                      // Shift+click: toggle this option on/off
                      const next = new Set(prev)
                      if (next.has(value as ComponentFilterStatus)) {
                        next.delete(value as ComponentFilterStatus)
                        if (next.size === 0) {
                          return new Set(defaultStatusOptions)
                        }
                      } else {
                        next.add(value as ComponentFilterStatus)
                      }
                      return next
                    }
                    // Click: select only this option, or reset to default if clicking the only selected option
                    if (prev.size === 1 && prev.has(value as ComponentFilterStatus)) {
                      return new Set(defaultStatusOptions)
                    }
                    return new Set([value as ComponentFilterStatus])
                  })
                },
              },
              {
                label: 'Categories',
                options: availableCategories.map(cat => ({ value: cat, label: cat, icon: categoryIconComponents[cat] })),
                selectedValues: categoryFilters as Set<string>,
                onToggle: (value, shiftKey) => {
                  setCategoryFilters(prev => {
                    if (shiftKey) {
                      // Shift+click: toggle this option on/off
                      const next = new Set(prev)
                      if (next.has(value as ComponentCategory)) {
                        next.delete(value as ComponentCategory)
                        if (next.size === 0) {
                          return new Set(availableCategories)
                        }
                      } else {
                        next.add(value as ComponentCategory)
                      }
                      return next
                    }
                    // Click: select only this option, or reset to all if clicking the only selected option
                    if (prev.size === 1 && prev.has(value as ComponentCategory)) {
                      return new Set(availableCategories)
                    }
                    return new Set([value as ComponentCategory])
                  })
                },
              },
              {
                label: 'Types',
                options: availableTypes.map(type => ({ value: type, label: type, icon: typeIconComponents[type] })),
                selectedValues: typeFilters as Set<string>,
                onToggle: (value, shiftKey) => {
                  setTypeFilters(prev => {
                    if (shiftKey) {
                      // Shift+click: toggle this option on/off
                      const next = new Set(prev)
                      if (next.has(value as ComponentType)) {
                        next.delete(value as ComponentType)
                        if (next.size === 0) {
                          return new Set(availableTypes)
                        }
                      } else {
                        next.add(value as ComponentType)
                      }
                      return next
                    }
                    // Click: select only this option, or reset to all if clicking the only selected option
                    if (prev.size === 1 && prev.has(value as ComponentType)) {
                      return new Set(availableTypes)
                    }
                    return new Set([value as ComponentType])
                  })
                },
              },
            ]}
          />
        </div>

        {/* Search Bar - Center (absolutely positioned to stay centered regardless of left/right content) */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            width: isSearchFocused ? '450px' : '300px',
            transition: 'width 200ms ease',
            margin: '-1px',
            padding: '1px',
          }}
        >
            {/* Border element - positioned absolutely to not affect layout */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '10px',
                border: '2px solid #d4d4d4',
                opacity: isSearchFocused ? 1 : 0,
                transition: 'opacity 200ms ease',
                pointerEvents: 'none',
              }}
            />
            {/* Inner search bar - always same size */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: searchTerm ? '#525252' : '#262626',
                borderRadius: '8px',
                padding: '8px 12px',
                width: '100%',
              }}
            >
              <Search className="h-4 w-4" style={{ color: '#d4d4d4', flexShrink: 0 }} />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setSearchTerm('')
                    searchInputRef.current?.blur()
                  } else if (e.key === 'Backspace' && searchTerm === '') {
                    searchInputRef.current?.blur()
                  }
                }}
                placeholder=""
                style={{
                  flex: 1,
                  background: 'none',
                  border: 'none',
                  outline: 'none',
                  fontFamily: "'Menlo', monospace",
                  fontSize: '12px',
                  fontWeight: 400,
                  color: '#fafafa',
                }}
              />
            </div>
        </div>

        {/* Export Status Button - Right */}
        <div style={{ flex: '0 0 auto', opacity: isSearchFocused ? 0 : 1, pointerEvents: isSearchFocused ? 'none' : 'auto', transition: 'opacity 200ms ease', zIndex: 1 }}>
          <button
            onClick={handleExportStatus}
            onMouseEnter={() => setExportButtonHovered(true)}
            onMouseLeave={() => { setExportButtonHovered(false); setExportButtonPressed(false) }}
            onMouseDown={() => setExportButtonPressed(true)}
            onMouseUp={() => setExportButtonPressed(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: exportButtonPressed ? '#404040' : exportButtonHovered ? '#171717' : '#0a0a0a',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontFamily: "'Figtree', sans-serif",
              fontSize: '14px',
              fontWeight: 630,
              color: copied ? '#00bc7d' : '#fafafa',
              padding: '4px 8px',
            }}
            title="Copy status data to clipboard (paste into ComponentsReviewRound3.json before pushing)"
          >
            {copied ? (
              <>
                <Check style={{ width: 14, height: 14 }} />
                Copied!
              </>
            ) : (
              <>
                <ClipboardCopy style={{ width: 14, height: 14 }} />
                Export Status
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-8">
        {searchTerm.trim() ? (
          searchSortedComponents.length > 0 ? (
            /* Flat list when searching */
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
                maxWidth: '750px',
                margin: '0 auto',
              }}
            >
              {searchSortedComponents.map((component, index) => (
                <ComponentCard
                  key={component.id}
                  component={component}
                  componentCategories={component.categories}
                  searchTerm={searchTerm}
                  isSelected={selectedIndex >= 0 && index === selectedIndex}
                  statusData={statusData}
                  onNavigate={onNavigate}
                  onStatusClick={handleStatusClick}
                  onCommentClick={handleCommentClick}
                />
              ))}
            </div>
          ) : (
            /* No results message */
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                fontFamily: "'Figtree', sans-serif",
                fontSize: '14px',
                fontWeight: 630,
                color: '#d4d4d4',
              }}
            >
              No matching components
            </div>
          )
        ) : filteredSections.length === 0 ? (
          /* No results from filters */
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: '16px',
            }}
          >
            <span
              style={{
                fontFamily: "'Figtree', sans-serif",
                fontSize: '14px',
                fontWeight: 630,
                color: '#d4d4d4',
              }}
            >
              No matching components
            </span>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                style={{
                  fontFamily: "'Figtree', sans-serif",
                  fontSize: '12px',
                  fontWeight: 630,
                  color: '#fafafa',
                  backgroundColor: '#262626',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : isTypeFilterActive ? (
          /* Grouped by type when filtering by specific types */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '25px', opacity: isSearchFocused ? 0 : 1, pointerEvents: isSearchFocused ? 'none' : 'auto', transition: 'opacity 200ms ease' }}>
            {filteredSections.map((section) => (
              <div key={section.title}>
                {/* Section Header */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#f5f5f5',
                    fontSize: '14px',
                    fontWeight: 400,
                    marginBottom: '8px',
                    gap: '8px',
                    textTransform: 'uppercase',
                  }}
                >
                  <span style={{ color: '#d4d4d4' }}>{typeIcons[section.title]}</span>
                  <span>{section.title}</span>
                </div>

                {/* Cards Grid */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                    gap: '12px',
                  }}
                >
                  {section.components.map((component) => (
                    <ComponentCard
                      key={component.id}
                      component={component}
                      componentCategories={component.categories}
                      searchTerm={searchTerm}
                      isSelected={selectedIndex >= 0 && getComponentIndex(component.id) === selectedIndex}
                      statusData={statusData}
                      onNavigate={onNavigate}
                      onStatusClick={handleStatusClick}
                      onCommentClick={handleCommentClick}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Default view: flat alphabetical list */
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: '12px',
              opacity: isSearchFocused ? 0 : 1,
              pointerEvents: isSearchFocused ? 'none' : 'auto',
              transition: 'opacity 200ms ease',
            }}
          >
            {flatSortedComponents.map((component, index) => (
              <ComponentCard
                key={component.id}
                component={component}
                componentCategories={component.categories}
                searchTerm={searchTerm}
                isSelected={selectedIndex >= 0 && index === selectedIndex}
                statusData={statusData}
                onNavigate={onNavigate}
                onStatusClick={handleStatusClick}
                onCommentClick={handleCommentClick}
              />
            ))}
          </div>
        )}
      </div>

      {/* Comment Popup */}
      {commentPopupComponentId && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 1000,
          }}
        >
          <div
            ref={commentPopupRef}
            style={{
              backgroundColor: '#171717',
              borderRadius: '12px',
              border: '1px solid #404040',
              padding: '16px',
              width: '400px',
              maxWidth: '90vw',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span
                style={{
                  fontFamily: "'Menlo', monospace",
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#fafafa',
                }}
              >
                {getComponentLabel(commentPopupComponentId)}
              </span>
              <button
                onClick={handleCommentCancel}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <X style={{ width: 16, height: 16, color: '#a1a1a1' }} />
              </button>
            </div>
            <textarea
              ref={commentTextareaRef}
              value={commentPopupValue}
              onChange={(e) => setCommentPopupValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleCommentSave()
                }
                if (e.key === 'Escape') {
                  handleCommentCancel()
                }
              }}
              placeholder="Add a comment... (Enter to save, Shift+Enter for new line)"
              rows={4}
              style={{
                width: '100%',
                backgroundColor: '#262626',
                border: '1px solid #525252',
                borderRadius: '8px',
                color: '#fafafa',
                padding: '10px',
                outline: 'none',
                fontFamily: "'Menlo', monospace",
                fontSize: '12px',
                lineHeight: '1.5',
                resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <span
                style={{
                  fontFamily: "'Figtree', sans-serif",
                  fontSize: '11px',
                  color: '#737373',
                  alignSelf: 'center',
                  marginRight: 'auto',
                }}
              >
                Enter to save · Shift+Enter for new line
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Helper to get a component label from its ID (used for comment popup title)
function getComponentLabel(componentId: string): string {
  const comp = componentDefinitions.find(c => c.id === componentId)
  return comp?.label || componentId
}

// Get ordered components based on current status (unreviewed first, then reviewed)
export function getUnifiedOrderedComponents(): { id: string; label: string; section: string }[] {
  const statusData = loadStatusData()

  const unreviewed: { id: string; label: string; section: string }[] = []
  const reviewed: { id: string; label: string; section: string }[] = []

  componentDefinitions.forEach(comp => {
    const item = { id: comp.id, label: comp.label, section: comp.types[0] || 'Misc' }
    if (isComponentFullyReviewed(comp.id, statusData)) {
      reviewed.push({ ...item, section: 'Reviewed' })
    } else {
      unreviewed.push(item)
    }
  })

  return [...unreviewed, ...reviewed]
}

// Export the flat list of all unified components for navigation
export const allUnifiedComponentsOrdered = componentDefinitions.map(comp => ({
  id: comp.id,
  label: comp.label,
  section: comp.types[0] || 'Misc'
}))
